// node_modules
import { FC, useContext, useEffect, useState } from "react";
// styles
import styles from "./conceptTechnologyFooter.module.scss";
// Components
import { FindestButton } from "Components";
// Contexts
import { CaseContext } from "Stores";
// Interfaces
import { ICaseSelectedConcept, ICaseSelectedTechnology, ICaseSetSelectedEntity } from "Interfaces";
// Enums
import { CaseActionTypeEnum, CasePageEnum, EntityTypeEnum } from "Enums";
// Types
import { TTechnology, TConcept } from "Types";
// Constants
import { ConceptConstants } from "Constants";
import { DeliveryLayoutContext } from "Providers";

type TResult = {
    type: EntityTypeEnum,
    result: TConcept | TTechnology
}

export const ConceptTechnologyFooter: FC = () => {
    // State
    const [flattenedResultList, setFlattenedResultList] = useState<TResult[]>([]);
    const [currentResultIndexInFlattenedList, setCurrentResultIndexInFlattenedList] = useState<number | undefined>(undefined);

    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { resetScrollCurrentMainScrolledContainer } = useContext(DeliveryLayoutContext);
    
    // Logic
    useEffect(() => {
        // safety-checks;
        if (!caseState.conceptTechnologyList ||
            !caseState.conceptTechnologyList.concepts) return;

        const newFlattenedResultList: TResult[] = [];

        for (const concept of caseState.conceptTechnologyList.concepts) {
            if (!concept) continue;
            newFlattenedResultList.push({ type: EntityTypeEnum.ConceptType, result: concept });

            if (!concept.technologies) continue;
            newFlattenedResultList.push(
                ...concept.technologies.map(
                    technology => {
                        return { type: EntityTypeEnum.TechnologyType, result: technology } as TResult;
                    }
                )
            );
        }

        if (caseState.conceptTechnologyList.additionalTechnologies) {
            const fixedAdditionalConcept: TConcept = {
                id: ConceptConstants.FIXED_ADDITIONAL_CONCEPT_ID,
                name: ConceptConstants.FIXED_ADDITIONAL_CONCEPT_NAME,
                technologies: []
            };

            newFlattenedResultList.push(
                { type: EntityTypeEnum.ConceptType, result: fixedAdditionalConcept },
                ...caseState.conceptTechnologyList.additionalTechnologies.map(
                    technology => {
                        return { type: EntityTypeEnum.TechnologyType, result: technology } as TResult;
                    }
                )
            );
        }

        setFlattenedResultList(newFlattenedResultList);
    }, [caseState.conceptTechnologyList]);

    useEffect(() => {
        if ((caseState.selectedConceptId && caseState.selectedTechnologyId) ||
            (!caseState.selectedConceptId && !caseState.selectedTechnologyId)) return;
        for (let index = 0; index < flattenedResultList.length; index++) {
            const element = flattenedResultList[index];
            if (caseState.selectedTechnologyId &&
            !caseState.selectedConceptId &&
            element.type === EntityTypeEnum.TechnologyType &&
            element.result.id === caseState.selectedTechnologyId) {
                setCurrentResultIndexInFlattenedList(index);
                return;
            } else if (
            caseState.selectedConceptId &&
            !caseState.selectedTechnologyId &&
            element.type === EntityTypeEnum.ConceptType &&
            element.result.id === caseState.selectedConceptId) {
                setCurrentResultIndexInFlattenedList(index);
                return;
            }
        }
    }, [caseState.selectedConceptId, caseState.selectedTechnologyId, flattenedResultList]);

    const toOverviewButtonOnClick = (): void => {
        resetScrollCurrentMainScrolledContainer();
        caseDispatch({
            type: CaseActionTypeEnum.SetCaseSelectedEntity, selectedEntityId: -1,
            selectedEntityName: CasePageEnum.Overview, selectedEntityNumbering: ""
        } as ICaseSetSelectedEntity);
    };

    const previousResultOnClick = (): void => {
        resetScrollCurrentMainScrolledContainer();
        // safety-checks;
        if (currentResultIndexInFlattenedList === undefined ||
            (currentResultIndexInFlattenedList < 1) ||
            !flattenedResultList ||
            flattenedResultList.length < 2) return;

        const newResult: TResult | undefined = flattenedResultList[currentResultIndexInFlattenedList - 1];

        if (!newResult) return;

        setCurrentResultIndexInFlattenedList(currentResultIndexInFlattenedList - 1);
        selectNewResult(newResult);
    };

    const nextResultOnClick = (): void => {
        resetScrollCurrentMainScrolledContainer();
        // safety-checks;
        if (currentResultIndexInFlattenedList === undefined ||
            !flattenedResultList ||
            (currentResultIndexInFlattenedList + 1 > flattenedResultList.length) ||
            flattenedResultList.length < 2) return;

        const newResult: TResult | undefined = flattenedResultList[currentResultIndexInFlattenedList + 1];

        if (!newResult) return;

        setCurrentResultIndexInFlattenedList(currentResultIndexInFlattenedList + 1);
        selectNewResult(newResult);
    };

    const selectNewResult = (newResult: TResult): void => {
        if (newResult && newResult.type === EntityTypeEnum.ConceptType && newResult.result) {
            const newConcept: TConcept = newResult.result as TConcept;
            caseDispatch({
                type: CaseActionTypeEnum.SetCaseSelectedConcept, selectedConceptId: newConcept.id,
                selectedEntityName: newConcept.name, selectedEntityNumbering: newConcept.numbering
            } as ICaseSelectedConcept);
        } else if (newResult && newResult.type === EntityTypeEnum.TechnologyType && newResult.result) {
            const newTechnology: TTechnology = newResult.result as TTechnology;
            caseDispatch({
                type: CaseActionTypeEnum.SetCaseSelectedTechnology,
                selectedTechnologyId: newTechnology.id,
                selectedOriginalTechnologyId: newTechnology.originalTechnologyId,
                selectedEntityName: newTechnology.name,
                selectedEntityNumbering: newTechnology.numbering
            } as ICaseSelectedTechnology);
        }
    };

    return (
        <div className={styles.conceptTechnologyFooter}>
            <div className={styles.leftColumn}>
                {currentResultIndexInFlattenedList !== undefined &&
                    (currentResultIndexInFlattenedList > 0) &&
                    flattenedResultList &&
                    flattenedResultList.length > 1 ?

                    <FindestButton
                        isFooterNavigation={"left"}
                        title={"Previous result"}
                        onClick={previousResultOnClick} />
                    :
                    null
                }
            </div>
            <div className={styles.centerColumn}>
                {currentResultIndexInFlattenedList !== undefined ?
                    <FindestButton
                        isFooterNavigation={"center"}
                        title={"To overview"}
                        onClick={toOverviewButtonOnClick} />
                    :
                    null
                }
            </div>
            <div className={styles.rightColumn}>
                {currentResultIndexInFlattenedList !== undefined &&
                    flattenedResultList &&
                    (currentResultIndexInFlattenedList + 1) < flattenedResultList.length &&
                    flattenedResultList.length > 1 ?
                    <FindestButton
                        isFooterNavigation={"right"}
                        title={"Next result"}
                        onClick={nextResultOnClick} />
                    :
                    null
                }
            </div>
        </div>
    );
}